import { Container, Graphics } from 'pixi.js';
import { TILE, type GeneratedMap } from './MapGen';
import { Palette } from '../render/Palette';
import type { FloorTheme } from '../render/themes';

/**
 * Static floor rendering, built once per floor: checkered floor tiles over a
 * wall-colored backdrop, with edge highlights on walls adjacent to floor.
 * Colors come from the per-biome theme (ME1); structure is unchanged — one
 * Graphics for the whole floor.
 */
export function buildMapGraphics(map: GeneratedMap, theme: FloorTheme): Container {
  const container = new Container();

  const g = new Graphics();
  // Backdrop (walls) covering the whole map + margin.
  g.rect(-TILE * 2, -TILE * 2, (map.w + 4) * TILE, (map.h + 4) * TILE).fill(theme.wall);

  // Floor tiles, checkered for movement readability.
  for (let ty = 0; ty < map.h; ty++) {
    for (let tx = 0; tx < map.w; tx++) {
      if (map.tiles[ty * map.w + tx] !== 1) continue;
      g.rect(tx * TILE, ty * TILE, TILE, TILE)
        .fill((tx + ty) % 2 === 0 ? theme.floor : theme.floorAlt);
    }
  }

  // Wall edge highlights: wall tiles bordering floor get a lit edge.
  for (let ty = 0; ty < map.h; ty++) {
    for (let tx = 0; tx < map.w; tx++) {
      if (map.tiles[ty * map.w + tx] === 1) continue;
      const floorBelow = ty + 1 < map.h && map.tiles[(ty + 1) * map.w + tx] === 1;
      const floorAbove = ty - 1 >= 0 && map.tiles[(ty - 1) * map.w + tx] === 1;
      const floorRight = tx + 1 < map.w && map.tiles[ty * map.w + tx + 1] === 1;
      const floorLeft = tx - 1 >= 0 && map.tiles[ty * map.w + tx - 1] === 1;
      if (floorBelow) g.rect(tx * TILE, ty * TILE + TILE - 5, TILE, 5).fill(theme.wallEdge);
      if (floorAbove) g.rect(tx * TILE, ty * TILE, TILE, 3).fill({ color: 0x000000, alpha: 0.35 });
      if (floorRight) g.rect(tx * TILE + TILE - 3, ty * TILE, 3, TILE).fill(theme.wallEdge);
      if (floorLeft) g.rect(tx * TILE, ty * TILE, 3, TILE).fill(theme.wallEdge);
    }
  }
  container.addChild(g);
  return container;
}

/**
 * Swirling descent portal: two counter-rotating spiral-arm layers over a
 * static glow ring. Geometry is baked once — animate() only sets rotations,
 * so there's no per-frame Graphics rebuild.
 */
export class ExitPortal extends Container {
  private readonly swirlA = spiralArms(3, 18, 2.5, 0.9);
  private readonly swirlB = spiralArms(4, 14, 1.8, 0.5);

  constructor() {
    super();
    const glow = new Graphics();
    for (const [r, a] of [[30, 0.08], [24, 0.14]] as const) {
      glow.circle(0, 0, r).fill({ color: Palette.gold, alpha: a });
    }
    glow.circle(0, 0, 19).stroke({ width: 2, color: Palette.gold, alpha: 0.35 });
    glow.circle(0, 0, 3.5).fill(Palette.gold);
    this.addChild(glow, this.swirlB, this.swirlA);
  }

  animate(time: number): void {
    this.swirlA.rotation = time * 1.8;
    this.swirlB.rotation = -time * 1.1;
    this.scale.set(1 + 0.06 * Math.sin(time * 3.2));
  }
}

/** Arms follow r = r0 → 5 over ~2.4 rad — an inward spiral polyline. */
function spiralArms(arms: number, r0: number, width: number, alpha: number): Graphics {
  const g = new Graphics();
  const steps = 12;
  for (let a = 0; a < arms; a++) {
    const base = (a / arms) * Math.PI * 2;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const theta = base + t * 2.4;
      const r = r0 - t * (r0 - 5);
      const x = Math.cos(theta) * r;
      const y = Math.sin(theta) * r;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width, color: Palette.gold, alpha });
  }
  return g;
}

/** The floor exit marker (a portal since the swirl pass — July 2026). */
export function drawExitHatch(): ExitPortal {
  return new ExitPortal();
}
